/**
 * Taaksjablonen — beheer van de sjablonen waaruit de wizard en de takengenerator
 * taken maken (besluit Joris, 6 sep 2026: "taaksjablonen mag naar onze database").
 *
 * Bron is de engine (/api/v1/taaksjablonen, Postgres). Op een omgeving waar de
 * engine die route nog niet heeft (productie tot de engine-overgang) toont het
 * scherm dat de sjablonen daar nog in Airtable staan.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, MenuItem, Checkbox, IconButton, Tooltip, Chip, Alert,
} from '@mui/material';
import { SectionHeader, T, Icon } from '../adminSettingsCommon';
import { AI_ENGINE_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { leesJson, meldFout } from '../../../utils/apiFout';
import { bevestig } from '../../../utils/bevestig';
import toast from 'react-hot-toast';

const API = `${AI_ENGINE_URL}/api/v1/taaksjablonen`;
const veld = { size: 'small', variant: 'outlined' };
const invoer = { style: { fontFamily: T.fontBody, fontSize: 13 } };
const kop = { fontFamily: T.font, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.outline };

async function api(pad, opties = {}, wat = 'Opslaan') {
  const r = await authFetch(`${API}${pad}`, { headers: { 'Content-Type': 'application/json' }, ...opties });
  return leesJson(r, wat);
}

/** Tekstveld dat pas bij blur/Enter opslaat — geen PATCH per toetsaanslag. */
function Veld({ waarde, onOpslaan, type = 'text', breedte, placeholder, multiline }) {
  const [v, setV] = useState(waarde ?? '');
  useEffect(() => { setV(waarde ?? ''); }, [waarde]);
  const klaar = () => {
    const nieuw = type === 'number' ? (v === '' ? null : Number(v)) : v;
    const oud = type === 'number' ? (waarde ?? null) : (waarde ?? '');
    if (nieuw !== oud) onOpslaan(nieuw);
  };
  return (
    <TextField {...veld} type={type} value={v} placeholder={placeholder} multiline={multiline} minRows={multiline ? 2 : undefined}
      onChange={(e) => setV(e.target.value)} onBlur={klaar}
      onKeyDown={(e) => { if (e.key === 'Enter' && !multiline) { e.preventDefault(); e.target.blur(); } }}
      inputProps={{ ...invoer, step: type === 'number' ? 'any' : undefined }} sx={{ width: breedte || '100%' }} />
  );
}

function Keuze({ waarde, opties, onOpslaan, breedte = 150, leeg }) {
  return (
    <TextField {...veld} select value={waarde ?? ''} onChange={(e) => onOpslaan(e.target.value || null)}
      inputProps={invoer} sx={{ width: breedte }} SelectProps={{ displayEmpty: !!leeg }}>
      {leeg && <MenuItem value=""><em>{leeg}</em></MenuItem>}
      {opties.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>{o}</MenuItem>)}
    </TextField>
  );
}

function Taak({ taak, meta, onWijzig, onVerwijder, onSubtaak }) {
  const [open, setOpen] = useState(false);
  const [nieuweSub, setNieuweSub] = useState('');
  const patch = (velden) => onWijzig(taak.id, velden);
  return (
    <Box sx={{ borderTop: `1px solid ${T.surfaceHigh}`, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '28px minmax(180px, 2fr) 150px 120px 80px 80px 130px 90px 60px 36px', gap: 1, alignItems: 'center' }}>
        <Tooltip title={open ? 'Subtaken verbergen' : `Subtaken (${taak.subtaken.length})`}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)} sx={{ p: 0.25 }}>
            <Icon name={open ? 'expand_more' : 'chevron_right'} size={18} />
          </IconButton>
        </Tooltip>
        <Veld waarde={taak.naam} onOpslaan={(v) => patch({ naam: v })} />
        <Keuze waarde={taak.type_uitvoerder} opties={meta.rollen} onOpslaan={(v) => patch({ type_uitvoerder: v })} />
        <Veld waarde={taak.type} onOpslaan={(v) => patch({ type: v })} placeholder="Type" />
        <Veld waarde={taak.zwaarte} type="number" onOpslaan={(v) => patch({ zwaarte: v })} placeholder="zw." />
        <Veld waarde={taak.uren} type="number" onOpslaan={(v) => patch({ uren: v })} placeholder="uren" />
        <Keuze waarde={taak.afhankelijkheid} opties={meta.afhankelijkheden} onOpslaan={(v) => patch({ afhankelijkheid: v || '' })} breedte={130} leeg="—" />
        <Tooltip title="Laatste taak van het sjabloon (bepaalt de einddatum)">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox size="small" checked={!!taak.laatste_taak} onChange={(e) => patch({ laatste_taak: e.target.checked })} sx={{ p: 0.5 }} />
            <Typography sx={{ fontSize: 11, color: T.secondary }}>laatste</Typography>
          </Box>
        </Tooltip>
        <Veld waarde={taak.sort} type="number" onOpslaan={(v) => patch({ sort: v })} placeholder="#" />
        <Tooltip title="Taak uit sjabloon verwijderen">
          <IconButton size="small" onClick={() => onVerwijder(taak)} sx={{ p: 0.25, color: T.outline, '&:hover': { color: '#b91c1c' } }}><Icon name="delete" size={18} /></IconButton>
        </Tooltip>
      </Box>
      {open && (
        <Box sx={{ ml: 4.5, mt: 0.75, mb: 0.5, pl: 1.5, borderLeft: `2px solid ${T.tertiaryContainer}` }}>
          <Typography sx={{ ...kop, mb: 0.5 }}>Subtaken</Typography>
          {taak.subtaken.map((s) => (
            <Box key={s.id} sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 70px 36px', gap: 1, alignItems: 'center', mb: 0.5 }}>
              <Veld waarde={s.naam} onOpslaan={(v) => onSubtaak('patch', s, { naam: v })} />
              <Veld waarde={s.order} type="number" onOpslaan={(v) => onSubtaak('patch', s, { order: v })} placeholder="#" />
              <IconButton size="small" onClick={() => onSubtaak('delete', s)} sx={{ p: 0.25, color: T.outline, '&:hover': { color: '#b91c1c' } }}><Icon name="close" size={16} /></IconButton>
            </Box>
          ))}
          <TextField {...veld} placeholder="+ Subtaak (Enter)" value={nieuweSub} onChange={(e) => setNieuweSub(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && nieuweSub.trim()) { onSubtaak('add', null, { naam: nieuweSub.trim() }, taak.id); setNieuweSub(''); } }}
            inputProps={invoer} sx={{ width: 320 }} />
        </Box>
      )}
    </Box>
  );
}

export default function Taaksjablonen() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('laden'); // laden | ok | nietbeschikbaar | fout
  const [gekozen, setGekozen] = useState(null);
  const [nieuwLabel, setNieuwLabel] = useState('');
  const [nieuwNiveau, setNieuwNiveau] = useState('Deliverable');
  const [nieuweTaak, setNieuweTaak] = useState('');

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/boom`);
      if (r.status === 404) { setStatus('nietbeschikbaar'); return; }
      const d = await leesJson(r, 'Sjablonen laden');
      setData(d); setStatus('ok');
      setGekozen((g) => g || d.labels[0]?.id || null);
    } catch (e) { setStatus('fout'); meldFout(e, 'Sjablonen laden'); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const label = useMemo(() => data?.labels.find((l) => l.id === gekozen) || null, [data, gekozen]);
  const meta = { rollen: data?.rollen || [], afhankelijkheden: data?.afhankelijkheden || [], niveaus: data?.niveaus || [] };
  const alleenLezen = data?.bron && data.bron !== 'pg';

  const doe = async (fn, wat) => { try { await fn(); await laad(); } catch (e) { meldFout(e, wat); } };

  const maakLabel = () => doe(async () => {
    const d = await api('/labels', { method: 'POST', body: JSON.stringify({ naam: nieuwLabel.trim(), niveau: nieuwNiveau }) }, 'Sjabloon aanmaken');
    setNieuwLabel(''); setGekozen(d.id); toast.success('Sjabloon aangemaakt');
  }, 'Sjabloon aanmaken');
  const wijzigLabel = (velden) => doe(() => api(`/labels/${label.id}`, { method: 'PATCH', body: JSON.stringify(velden) }, 'Sjabloon opslaan'), 'Sjabloon opslaan');
  const verwijderLabel = async () => {
    if (!(await bevestig({ titel: `Sjabloon "${label.naam}" verwijderen?`, tekst: `Met ${label.taken.length} taken en hun subtaken. Bestaande projecttaken blijven staan.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    doe(async () => { await api(`/labels/${label.id}`, { method: 'DELETE' }, 'Sjabloon verwijderen'); setGekozen(null); toast.success('Sjabloon verwijderd'); }, 'Sjabloon verwijderen');
  };
  const maakTaak = () => doe(async () => {
    await api(`/labels/${label.id}/taken`, { method: 'POST', body: JSON.stringify({ naam: nieuweTaak.trim() }) }, 'Taak toevoegen');
    setNieuweTaak('');
  }, 'Taak toevoegen');
  const wijzigTaak = (id, velden) => doe(() => api(`/taken/${id}`, { method: 'PATCH', body: JSON.stringify(velden) }, 'Taak opslaan'), 'Taak opslaan');
  const verwijderTaak = async (t) => {
    if (!(await bevestig({ titel: `Taak "${t.naam}" uit het sjabloon halen?`, tekst: t.subtaken.length ? `Inclusief ${t.subtaken.length} subtaken.` : 'Dit raakt alleen het sjabloon.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    doe(() => api(`/taken/${t.id}`, { method: 'DELETE' }, 'Taak verwijderen'), 'Taak verwijderen');
  };
  const subtaak = async (actie, s, velden, taakId) => {
    if (actie === 'delete' && !(await bevestig({ titel: `Subtaak "${s.naam}" verwijderen?`, tekst: 'Dit raakt alleen het sjabloon.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    return doe(() => {
      if (actie === 'add') return api(`/taken/${taakId}/subtaken`, { method: 'POST', body: JSON.stringify(velden) }, 'Subtaak toevoegen');
      if (actie === 'patch') return api(`/subtaken/${s.id}`, { method: 'PATCH', body: JSON.stringify(velden) }, 'Subtaak opslaan');
      return api(`/subtaken/${s.id}`, { method: 'DELETE' }, 'Subtaak verwijderen');
    }, 'Subtaak');
  };

  if (status === 'laden') return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  if (status === 'nietbeschikbaar') {
    return (
      <Box>
        <SectionHeader title="Taaksjablonen" subtitle="Sjablonen waaruit de wizard en de takengenerator taken maken." />
        <Alert severity="info" sx={{ fontFamily: T.fontBody }}>
          In deze omgeving staan de sjablonen nog in Airtable (tabellen StandardTasksLabel, StandardTasks en StandardSubtasks).
          Zodra de engine hier de nieuwe versie draait, beheer je ze op deze plek. Op test.fullframe.nl werkt dat al.
        </Alert>
      </Box>
    );
  }
  if (status === 'fout' || !data) {
    return <Box><SectionHeader title="Taaksjablonen" /><Button onClick={laad} variant="outlined" sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw proberen</Button></Box>;
  }

  const perNiveau = meta.niveaus.map((n) => ({ niveau: n, labels: data.labels.filter((l) => l.niveau === n) }));
  const overig = data.labels.filter((l) => !meta.niveaus.includes(l.niveau));

  return (
    <Box>
      <SectionHeader title="Taaksjablonen"
        subtitle="Sjablonen waaruit de wizard en de takengenerator taken maken. Wijzigingen gelden voor nieuwe projecten; bestaande taken veranderen niet." />
      {alleenLezen && <Alert severity="warning" sx={{ mb: 2 }}>Deze omgeving leest nog uit Airtable; wijzigen kan hier niet.</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
        <Box sx={{ bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`, borderRadius: 2, p: 1.5, boxShadow: T.shadow }}>
          {[...perNiveau, ...(overig.length ? [{ niveau: 'Overig', labels: overig }] : [])].map((g) => (
            <Box key={g.niveau} sx={{ mb: 1.5 }}>
              <Typography sx={{ ...kop, px: 1, mb: 0.5 }}>{g.niveau} · {g.labels.length}</Typography>
              {g.labels.map((l) => (
                <Box key={l.id} onClick={() => setGekozen(l.id)}
                  sx={{ px: 1, py: 0.6, borderRadius: 1, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 1,
                    bgcolor: gekozen === l.id ? T.tertiaryContainer : 'transparent', '&:hover': { bgcolor: gekozen === l.id ? T.tertiaryContainer : T.surfaceLow } }}>
                  <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: 13.5, fontWeight: gekozen === l.id ? 700 : 500, color: T.text }}>{l.naam || '(naamloos)'}</Typography>
                  <Typography sx={{ fontSize: 12, color: T.outline, flexShrink: 0 }}>{l.taken.length}</Typography>
                </Box>
              ))}
            </Box>
          ))}
          {!alleenLezen && (
            <Box sx={{ borderTop: `1px solid ${T.surfaceHigh}`, pt: 1.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField {...veld} placeholder="Nieuw sjabloon…" value={nieuwLabel} onChange={(e) => setNieuwLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && nieuwLabel.trim()) maakLabel(); }} inputProps={invoer} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Keuze waarde={nieuwNiveau} opties={meta.niveaus} onOpslaan={(v) => setNieuwNiveau(v || 'Deliverable')} breedte={140} />
                <Button size="small" variant="contained" disableElevation disabled={!nieuwLabel.trim()} onClick={maakLabel}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.tertiary, '&:hover': { bgcolor: '#4f4670' } }}>Toevoegen</Button>
              </Box>
            </Box>
          )}
        </Box>

        {label ? (
          <Box sx={{ bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`, borderRadius: 2, p: 2.5, boxShadow: T.shadow, minWidth: 0, overflowX: 'auto' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 150px 110px auto', gap: 1.5, alignItems: 'start', mb: 1.5 }}>
              <Box><Typography sx={{ ...kop, mb: 0.5 }}>Naam</Typography><Veld waarde={label.naam} onOpslaan={(v) => wijzigLabel({ naam: v })} /></Box>
              <Box><Typography sx={{ ...kop, mb: 0.5 }}>Niveau</Typography><Keuze waarde={label.niveau} opties={meta.niveaus} onOpslaan={(v) => wijzigLabel({ niveau: v })} /></Box>
              <Box><Typography sx={{ ...kop, mb: 0.5 }}>Blokvolgorde</Typography><Veld waarde={label.blok_volgorde} type="number" onOpslaan={(v) => wijzigLabel({ blok_volgorde: v })} /></Box>
              <Box sx={{ pt: 2.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                {label.uit_airtable && <Tooltip title="Oorspronkelijk uit Airtable overgenomen; wijzigingen gelden alleen hier"><Chip size="small" label="uit Airtable" sx={{ fontSize: 11 }} /></Tooltip>}
                {!alleenLezen && <Button size="small" color="error" onClick={verwijderLabel} sx={{ textTransform: 'none', fontWeight: 700 }}>Verwijderen</Button>}
              </Box>
            </Box>
            <Typography sx={{ ...kop, mb: 0.5 }}>Omschrijving</Typography>
            <Veld waarde={label.omschrijving} multiline onOpslaan={(v) => wijzigLabel({ omschrijving: v })} placeholder="Wanneer gebruik je dit sjabloon?" />

            <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: '28px minmax(180px, 2fr) 150px 120px 80px 80px 130px 90px 60px 36px', gap: 1, pb: 0.5 }}>
              {['', 'Taak', 'Rol', 'Type', 'Zwaarte', 'Uren', 'Afhankelijk', '', 'Volg.', ''].map((h, i) => <Typography key={i} sx={kop}>{h}</Typography>)}
            </Box>
            {label.taken.map((t) => <Taak key={t.id} taak={t} meta={meta} onWijzig={wijzigTaak} onVerwijder={verwijderTaak} onSubtaak={subtaak} />)}
            {!label.taken.length && <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, color: T.secondary, py: 1 }}>Nog geen taken in dit sjabloon.</Typography>}
            {!alleenLezen && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                <TextField {...veld} placeholder="+ Taak toevoegen (Enter)" value={nieuweTaak} onChange={(e) => setNieuweTaak(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && nieuweTaak.trim()) maakTaak(); }} inputProps={invoer} sx={{ width: 360 }} />
                <Button size="small" variant="outlined" disabled={!nieuweTaak.trim()} onClick={maakTaak} sx={{ textTransform: 'none', fontWeight: 700 }}>Toevoegen</Button>
              </Box>
            )}
            <Typography sx={{ fontFamily: T.fontBody, fontSize: 12, color: T.outline, mt: 2 }}>
              Zwaarte = stap in de urenformule (0 = vast aantal uren, zie kolom Uren). Rol bepaalt het taaktype als je die leeg laat. Wijzigingen worden direct bewaard.
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: T.fontBody, color: T.secondary, p: 2 }}>Kies links een sjabloon of maak een nieuw sjabloon.</Typography>
        )}
      </Box>
    </Box>
  );
}
